/**
 * Seasonal media helpers.
 *
 * Image data is stored on the same `cms_sections.content` row:
 *   { type: "image", value: <summerUrl>, alt: <summerAlt>,
 *     seasonal?: true,
 *     valueWinter?: <winterUrl>, altWinter?: <winterAlt>, ... }
 *
 * Videos use two keys (`id.src`, `id.poster`) plus an optional pair
 * (`id.srcWinter`, `id.posterWinter`) and a flag (`id.seasonal`).
 */

export type Season = "summer" | "winter";

export type SeasonalImageStored = {
  value: string;
  alt?: string;
  seasonal?: boolean;
  valueWinter?: string;
  altWinter?: string;
};

/**
 * Pick the appropriate URL/alt for the active season.
 * Falls back to the summer/default value if winter is enabled but empty.
 */
export function pickSeasonal(
  data: SeasonalImageStored | null | undefined,
  season: Season,
  fallback: { src: string; alt?: string }
): { src: string; alt?: string } {
  if (!data) return { src: fallback.src, alt: fallback.alt };
  if (data.seasonal && season === "winter") {
    return {
      src: data.valueWinter || data.value || fallback.src,
      alt: data.altWinter ?? data.alt ?? fallback.alt,
    };
  }
  return {
    src: data.value || fallback.src,
    alt: data.alt ?? fallback.alt,
  };
}

/**
 * Compute the active season.
 * In auto mode the dates are full ISO timestamps. The most recent past
 * switch-date wins. If both are in the future, fall back to `current`.
 */
export function computeSeason(settings: {
  mode?: "manual" | "auto";
  current?: Season;
  summer_start?: string; // ISO datetime
  winter_start?: string; // ISO datetime
} | null | undefined, now: Date = new Date()): Season {
  if (!settings || settings.mode === "manual") {
    return settings?.current === "winter" ? "winter" : "summer";
  }
  const s = settings.summer_start ? new Date(settings.summer_start) : null;
  const w = settings.winter_start ? new Date(settings.winter_start) : null;
  const sPast = s && !isNaN(s.getTime()) && s <= now ? s : null;
  const wPast = w && !isNaN(w.getTime()) && w <= now ? w : null;
  if (sPast && wPast) return sPast > wPast ? "summer" : "winter";
  if (sPast) return "summer";
  if (wPast) return "winter";
  return settings.current === "winter" ? "winter" : "summer";
}

// Defaults: next 15. April 08:00 and 15. October 08:00 of current/next year.
function defaultIso(month: number, day: number): string {
  const now = new Date();
  const year = now.getFullYear();
  const d = new Date(year, month - 1, day, 8, 0, 0);
  if (d < now) d.setFullYear(year + 1);
  return d.toISOString();
}

export const DEFAULT_SEASON_SETTINGS = {
  mode: "manual" as const,
  current: "summer" as Season,
  summer_start: defaultIso(4, 15),
  winter_start: defaultIso(10, 15),
};
